/**
 * Group flat location[] rows by assignment_number for AssignmentBlock / Task Detail UI.
 *
 * Legacy rows may omit assignment_number. In that case, locations that share the same
 * preparer + approver + sign-off + skipValidations fingerprint are one assignment;
 * different fingerprints become separate assignments (instead of incorrectly overwriting each other).
 *
 * Per-row `glcode` lists are unioned onto the assignment block. Split payloads
 * persist only the GLs mapped to each location; replacing instead of unioning
 * would drop earlier rows' accounts on edit hydrate.
 */
import { assignmentRolesFromLocation } from './assignmentRoles.js';
import { resolveEnforceThreshold } from '@/modules/financialclose/utils/enforceThresholdHelpers';

export function groupLocationsByAssignmentNumber(locationArr = [], taskType) {
  const rows = locationArr || [];
  const fingerprintToNumber = new Map();
  let nextSynthetic = 1;

  // Reserve numbers already used by explicit assignment_number values.
  rows.forEach((loc) => {
    const n = Number(loc?.assignment_number);
    if (Number.isFinite(n) && n >= 1) {
      nextSynthetic = Math.max(nextSynthetic, n + 1);
    }
  });

  const groups = new Map();

  rows.forEach((loc) => {
    let assignmentNumber =
      loc?.assignment_number != null && loc.assignment_number !== ''
        ? Number(loc.assignment_number)
        : null;

    if (!Number.isFinite(assignmentNumber) || assignmentNumber < 1) {
      const fingerprint = assignmentFingerprint(loc, taskType);
      if (fingerprintToNumber.has(fingerprint)) {
        assignmentNumber = fingerprintToNumber.get(fingerprint);
      } else {
        assignmentNumber = nextSynthetic;
        fingerprintToNumber.set(fingerprint, assignmentNumber);
        nextSynthetic += 1;
      }
    }

    const locationId = normalizeLocationId(loc);

    if (!groups.has(assignmentNumber)) {
      const roles = assignmentRolesFromLocation(loc);
      groups.set(assignmentNumber, {
        assignment_number: assignmentNumber,
        location_ids: [],
        preparer: [...roles.preparer],
        approver: [...roles.approver],
        requires_sign_off: roles.requires_sign_off,
        sign_off_approvers: [...roles.sign_off_approvers],
        enforceThreshold: resolveEnforceThreshold(loc, taskType),
        auto_approve: loc?.auto_approve != null ? !!loc.auto_approve : false,
        glcodes: [],
      });
    }

    const group = groups.get(assignmentNumber);

    if (locationId && !group.location_ids.includes(locationId)) {
      group.location_ids.push(locationId);
    }

    const roles = assignmentRolesFromLocation(loc);
    if (roles.preparer.length) {
      group.preparer = [...roles.preparer];
    }
    if (roles.approver.length) {
      group.approver = [...roles.approver];
    }
    if (loc?.requires_sign_off != null || loc?.user_roles?.length) {
      group.requires_sign_off = roles.requires_sign_off;
      group.sign_off_approvers = [...roles.sign_off_approvers];
    }
    if (loc?.enforceThreshold != null || typeof loc?.skipValidations === 'boolean') {
      group.enforceThreshold = resolveEnforceThreshold(loc, taskType);
    }
    if (loc?.auto_approve != null) {
      group.auto_approve = !!loc.auto_approve;
    }

    const gl = loc?.glcode || loc?.glcodes;
    if (gl?.length) {
      const incoming = Array.isArray(gl) ? gl : [gl];
      group.glcodes = normalizeGlCodes([...group.glcodes, ...incoming]);
    }
  });

  return Array.from(groups.values()).sort(
    (a, b) => a.assignment_number - b.assignment_number
  );
}

function assignmentFingerprint(loc = {}, taskType) {
  const roles = assignmentRolesFromLocation(loc);
  const preparer = roles.preparer.slice().sort().join(',');
  const approver = roles.approver.slice().sort().join(',');
  const signOff = roles.sign_off_approvers.slice().sort().join(',');
  const skip = String(resolveEnforceThreshold(loc, taskType));
  const requiresSignOff = String(!!roles.requires_sign_off);
  return `${preparer}|${approver}|${skip}|${requiresSignOff}|${signOff}`;
}

function normalizeLocationId(loc) {
  if (loc == null) return '';

  // Prefer location_id — Mongo subdocs may also have their own _id (not the location master id).
  const fromLocationId = toIdString(loc.location_id);
  if (fromLocationId) return fromLocationId;

  const fromLocation = toIdString(loc.location);
  if (fromLocation) return fromLocation;

  return '';
}

function toIdString(raw) {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'object') {
    const id = raw._id || raw.id;
    return id != null && id !== '' ? String(id) : '';
  }
  const asString = String(raw);
  // Only accept Mongo ObjectId-shaped values (avoid treating display names as ids)
  return /^[a-fA-F0-9]{24}$/.test(asString) ? asString : '';
}

function normalizeGlCodes(gls) {
  if (!Array.isArray(gls)) return [];
  const seen = new Set();
  const out = [];

  gls.forEach((g) => {
    let code = '';
    if (g == null || g === '') return;
    if (typeof g === 'object') {
      const raw = g.glcode || g._id || g.id;
      code = raw != null ? String(raw) : '';
    } else {
      code = String(g);
    }
    // Drop corrupted uploads like "[object Object]"
    if (!code || code === '[object Object]' || seen.has(code)) return;
    seen.add(code);
    out.push(code);
  });

  return out;
}
